import { connect } from 'react-redux';
import type { GreekLine } from 'state/quotes/details/details.model';
import type { SolveResultFormat } from 'state/quotes/results/result.model';
import {
  getFormQuoteDetails,
  getFormQuoteFxSpot,
  getFormQuoteResult,
  getFormQuoteYield,
  getQuoteCurrency,
  getQuotePriceInCurrency,
  getQuotePriceInPercent,
  getQuotePricerToBookingInvertedFlow,
  getQuoteRemunerationMode,
  getQuoteSolvingMode,
  getSidePanelTradeWayOrDefault,
} from 'state/selectors';
import { getTradeInfosByWorkspaceTab } from 'state/selectors/common/temp';
import type { AppState } from 'state/state.model';
import type { QuoteWorkspaceType } from 'state/workspaces/model';

import { type FlowWay, getCashFlowWay } from '../cashFlowWayUtils';

export interface OwnProps {
  tabId: string;
  workspaceType: QuoteWorkspaceType;
}

export interface StateProps {
  solvingMode: string;
  isMultipleSolvingMode: boolean;
  solveResult: number;
  solveResultFormat?: SolveResultFormat;
  tradeAuthStartTimeInMillis: number;
  tradeAuthDurationInMillis: number;
  flowWay: FlowWay;
  fxSpot: GreekLine | null;
  priceInPercent?: number;
  priceInCurrency?: number;
  currency?: string;
  isExecOnlyAddon?: boolean;
  quoteYield?: string;
}

export type QuotedState = StateProps & OwnProps;

const mapStateToProps = (state: AppState, { tabId, workspaceType }: OwnProps): StateProps => {
  const solvingMode = getQuoteSolvingMode(state, tabId);
  const { tradeAuthStartTimeInMillis, tradeAuthDurationInMillis } = getTradeInfosByWorkspaceTab(
    state,
    workspaceType,
    tabId,
  );
  const quoteResult = getFormQuoteResult(state, workspaceType, tabId)!;
  const solveResult = quoteResult?.solveResult!;
  const solveResultFormat = quoteResult?.solveResultFormat;
  const priceInPercent = getQuotePriceInPercent(state, tabId);
  const priceInCurrency = getQuotePriceInCurrency(state, workspaceType, tabId);
  const currency = getQuoteCurrency(state, workspaceType, tabId);
  const quoteDetails = getFormQuoteDetails(state, workspaceType, tabId);
  const fxSpot = getFormQuoteFxSpot(state, workspaceType, tabId);
  const quoteYield = getFormQuoteYield(state, workspaceType, tabId);
  const isExecOnlyAddon = quoteResult.isExecOnlyAddon;

  const remunerationMode = getQuoteRemunerationMode(state, tabId);
  const tradeWay = getSidePanelTradeWayOrDefault(state, tabId);
  const pricerToBookingInvertedFlow = getQuotePricerToBookingInvertedFlow(state, workspaceType, tabId);
  const cashFlowWay = getCashFlowWay(priceInPercent, remunerationMode, tradeWay, pricerToBookingInvertedFlow);

  return {
    solveResult,
    solveResultFormat,
    solvingMode,
    isMultipleSolvingMode: quoteDetails?.IsMultipleSolvingMode ?? false,
    tradeAuthStartTimeInMillis: tradeAuthStartTimeInMillis!,
    tradeAuthDurationInMillis: tradeAuthDurationInMillis!,
    fxSpot,
    priceInPercent,
    priceInCurrency,
    currency,
    isExecOnlyAddon,
    flowWay: cashFlowWay,
    quoteYield,
  };
};

export const connectQuotedTradeable = connect(mapStateToProps);
